import { Link } from "@tanstack/react-router"
import {
	IconCircleCheckFill18 as CircleCheck,
	IconTriangleWarningFill18 as Warning,
} from "nucleo-ui-fill-18"
import { IconXmarkOutline18 as XIcon } from "nucleo-ui-outline-18"

import { IconButton, Loader } from "@productlane/ui"

import { api } from "@/lib/api"

const POLL_MS = 3000

export interface SiteImportBannerStatus {
	state: "running" | "done" | "failed"
	url: string
	importedCount?: number | null
	imagesLeftBehind?: number | null
	published?: boolean | null
	message?: string | null
}

export function siteImportHost(url: string) {
	try {
		return new URL(url).hostname
	} catch {
		return url
	}
}

export function siteImportBannerText(status: SiteImportBannerStatus) {
	const host = siteImportHost(status.url)

	if (status.state === "running") {
		return `Importing articles from ${host}.`
	}

	if (status.state === "failed") {
		return status.message ?? `Couldn't read ${host}.`
	}

	const count = status.importedCount ?? 0
	const leftBehind = status.imagesLeftBehind ?? 0
	const images =
		leftBehind > 0
			? ` ${leftBehind} image${leftBehind === 1 ? "" : "s"} stayed on the old site.`
			: ""

	return `Imported ${count} article${count === 1 ? "" : "s"} from ${host}${status.published ? ", now live" : " as drafts"}.${images}`
}

export function StatusPill({
	state,
	text,
	openTo,
	dismissLabel,
	onDismiss,
}: {
	state: "running" | "done" | "failed"
	text: string
	openTo: string
	dismissLabel: string
	onDismiss: () => void
}) {
	return (
		<div className="pointer-events-auto flex h-9 max-w-[min(680px,100%)] items-center gap-2 rounded-full bg-green-50 pl-3.5 pr-1.5 text-[13px] shadow-lg ring-1 ring-green-600/15 dark:bg-green-500/15 dark:ring-green-400/20">
			{state === "running" && <Loader className="h-3.5 w-3.5 flex-none" />}
			{state === "done" && (
				<CircleCheck className="h-4 w-4 flex-none text-green-600 dark:text-green-400" />
			)}
			{state === "failed" && (
				<Warning className="h-4 w-4 flex-none text-green-700 dark:text-green-400" />
			)}

			<span className="min-w-0 truncate px-2 text-green-800 dark:text-green-200">
				{text}
			</span>

			{state === "done" && (
				<Link
					to={openTo}
					className="flex-none rounded-full px-2 py-0.5 text-green-900 hover:bg-green-100 dark:text-green-100 dark:hover:bg-green-500/20"
				>
					Open
				</Link>
			)}

			{state !== "running" && (
				<IconButton
					variant="ghost"
					size="sm"
					isRounded
					Icon={XIcon}
					aria-label={dismissLabel}
					className="flex-none text-green-800 dark:text-green-200"
					onClick={onDismiss}
				/>
			)}
		</div>
	)
}

export function SiteImportBannerPill({
	status,
	onDismiss,
}: {
	status: SiteImportBannerStatus
	onDismiss: () => void
}) {
	return (
		<StatusPill
			state={status.state}
			text={siteImportBannerText(status)}
			openTo="/edit-docs"
			dismissLabel="Dismiss import status"
			onDismiss={onDismiss}
		/>
	)
}

export function SiteImportBannerView({
	status,
	onDismiss,
}: {
	status: SiteImportBannerStatus
	onDismiss: () => void
}) {
	return (
		<div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4">
			<SiteImportBannerPill status={status} onDismiss={onDismiss} />
		</div>
	)
}

export function SiteImportBanner() {
	const context = api.useUtils()
	const { data: status } = api.workspace.helpCenterSiteImportStatus.useQuery(
		{},
		{
			refetchInterval: (data) => (data?.state === "running" ? POLL_MS : false),
			refetchOnWindowFocus: true,
		},
	)
	const dismiss = api.workspace.dismissHelpCenterSiteImport.useMutation({
		onSuccess: async () => {
			await context.workspace.helpCenterSiteImportStatus.invalidate()
		},
	})

	if (!status) return null

	return (
		<SiteImportBannerPill
			status={status}
			onDismiss={() => dismiss.mutate({})}
		/>
	)
}
